import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { logError } from '@zg-rentals/log-error';
import { useWebSocketConnection } from './useWebSocketConnection';
import type { WebSocketLogger, WebSocketMessage } from './types';

type WebSocketContextType = {
  /** Increments each time the WebSocket reconnects after an unexpected close. Track changes to trigger recovery actions (e.g., refetch stale data). */
  errorRecoveryCount: number;
  /** True while the WebSocket is in the CONNECTING state. */
  isConnecting: boolean;
  /** True when the WebSocket is OPEN, the user is authenticated, and the feature is enabled. This is the primary flag consumers should check before relying on real-time data. */
  isWebSocketEnabledAndReady: boolean;
  /** The most recently received WebSocket message, or null if none yet. */
  lastMessage: WebSocketMessage | null;
  /** The authenticated user token returned by the server, or null if not yet authenticated. */
  authenticatedUserToken: string | null;
  /**
   * Register a callback to receive all incoming WebSocket messages.
   * Returns an unsubscribe function — call it in your effect cleanup.
   *
   * @example
   * useEffect(() => {
   *   if (!isWebSocketEnabledAndReady) return;
   *   return subscribeToMessages((message) => {
   *     if (message.event.type === 'newMessage') refetch();
   *   });
   * }, [isWebSocketEnabledAndReady, subscribeToMessages]);
   */
  subscribeToMessages: (callback: (message: WebSocketMessage) => void | Promise<void>) => () => void;
};

const WebSocketContext = createContext<WebSocketContextType | null>(null);

/**
 * Access the WebSocket context. Must be used within a `<WebSocketProvider>`.
 * Throws if called outside the provider tree.
 */
export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

export type WebSocketProviderProps = {
  children: React.ReactNode;
  /**
   * Topic suffixes to subscribe to once authenticated. The user token is
   * prepended automatically (e.g., `['|inbox:renter']` becomes
   * `'<userToken>|inbox:renter'`).
   *
   * For Satellite apps, use `getTopicSuffixes()` from
   * `@zg-rentals/satellite-web-socket-helpers` to build these.
   */
  topicSuffixes: Array<string>;
  /** Full WebSocket server URL (e.g., `'wss://www.zillow.com/rental/satellite/ws/services'`). */
  websocketUrl: string;
  /** Whether the current user is authenticated. The WebSocket will not connect until this is true. */
  isAuthorized: boolean;
  /** Feature flag — set to false to disable WebSocket entirely and fall back to polling. */
  isEnabled: boolean;
  /** Enable verbose debug logging via the provided `logger`. */
  debug?: boolean;
  /** Logger instance for debug and lifecycle logging. Compatible with `bootstrap.logger.child(...)`. */
  logger?: WebSocketLogger;
  /** Extra context merged into close/reconnect/error log entries (e.g., `{ isMobileDevice, isMobileView }`). */
  loggerContext?: Record<string, unknown>;
};

/**
 * Manages a WebSocket connection and exposes it to the React tree via context.
 *
 * Handles the full lifecycle: connecting, authenticating (via `id|` handshake),
 * subscribing to topics, dispatching incoming messages to subscribers, and
 * reconnecting with exponential backoff on failure.
 *
 * Place this near the top of your component tree. Child components access
 * the connection via `useWebSocketContext()`.
 */
export const WebSocketProvider = ({
  children,
  topicSuffixes,
  websocketUrl,
  isAuthorized,
  isEnabled,
  debug,
  logger,
  loggerContext,
}: WebSocketProviderProps) => {
  const messageCallbacksRef = useRef<Set<(message: WebSocketMessage) => void | Promise<void>>>(new Set());

  const subscribeToMessages = useCallback((callback: (message: WebSocketMessage) => void | Promise<void>) => {
    messageCallbacksRef.current.add(callback);

    // Return unsubscribe function
    return () => {
      messageCallbacksRef.current.delete(callback);
    };
  }, []);

  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    messageCallbacksRef.current.forEach((callback) => {
      try {
        // Catch rejections from async callbacks to prevent unhandled promise errors.
        const result = callback(message);
        if (result instanceof Promise) {
          result.catch((error: unknown) => {
            logError({ error, context: 'Error in async WebSocket message callback' });
          });
        }
      } catch (error) {
        logError({ error, context: 'Error in WebSocket message callback' });
      }
    });
  }, []);

  const webSocketResult = useWebSocketConnection({
    websocketUrl,
    isAuthorized,
    isEnabled,
    debug,
    logger,
    loggerContext,
    topicSuffixes,
    onNewMessage: handleWebSocketMessage,
  });

  const contextValue: WebSocketContextType = useMemo(
    () => ({
      errorRecoveryCount: webSocketResult.errorRecoveryCount,
      isConnecting: webSocketResult.isConnecting,
      isWebSocketEnabledAndReady: webSocketResult.isWebSocketEnabledAndReady,
      lastMessage: webSocketResult.lastMessage,
      authenticatedUserToken: webSocketResult.authenticatedUserToken,
      subscribeToMessages,
    }),
    [
      webSocketResult.errorRecoveryCount,
      webSocketResult.isConnecting,
      webSocketResult.isWebSocketEnabledAndReady,
      webSocketResult.lastMessage,
      webSocketResult.authenticatedUserToken,
      subscribeToMessages,
    ],
  );

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};
